import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

const SORT_OPTIONS = [
  { value: 'championships', label: 'Championships' },
  { value: 'win_pct',       label: 'Win %' },
  { value: 'wins',          label: 'Total Wins' },
  { value: 'points',        label: 'Points For' },
];

function ChampBadge({ count }) {
  if (!count) return null;
  return (
    <span className="lor-champ-badge">
      <Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {count}×
    </span>
  );
}

function FormerBadge() {
  return <span className="lor-former-badge">Former</span>;
}

function StreakPill({ streak }) {
  if (!streak || streak.count === 0) return null;
  const isW = streak.type === 'W';
  return (
    <span className={`lor-streak lor-streak--${isW ? 'w' : 'l'}`}>
      {streak.type}{streak.count}
    </span>
  );
}

function ManagerRow({ manager, rank, isFormer }) {
  const [expanded, setExpanded] = useState(false);
  const {
    display_name, seasons_played, total_wins, total_losses, win_pct,
    total_pf, avg_ppg, championships, championship_seasons,
    finals_appearances, playoff_appearances, best_season, worst_season,
    current_streak,
  } = manager;

  return (
    <>
      <tr
        className="lor-managers-row lor-managers-row--clickable"
        onClick={() => setExpanded(e => !e)}
      >
        <td className="lor-rank">{rank}</td>
        <td>
          <div className="lor-manager-cell">
            <div>
              <div className="lor-manager-name">
                {display_name}
                {championships > 0 && <ChampBadge count={championships} />}
                {isFormer && <FormerBadge />}
              </div>
              <div className="lor-team-name">{seasons_played} season{seasons_played !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </td>
        <td>
          <span className="lor-wins">{total_wins}</span>
          <span style={{ color: '#444' }}>-</span>
          <span className="lor-losses">{total_losses}</span>
        </td>
        <td style={{ color: '#eee', fontWeight: 600 }}>{(win_pct * 100).toFixed(1)}%</td>
        <td className="lor-pf">{total_pf.toFixed(0)}</td>
        <td style={{ color: '#666', fontSize: '0.8rem' }}>{avg_ppg.toFixed(1)}</td>
        <td><StreakPill streak={current_streak} /></td>
        <td className="lor-managers-expand">{expanded ? '▲' : '▼'}</td>
      </tr>
      {expanded && (
        <tr className="lor-managers-detail-row">
          <td colSpan={8}>
            <div className="lor-managers-detail">
              <div className="lor-managers-detail-grid">
                <div className="lor-managers-stat">
                  <div className="lor-managers-stat-label">Championships</div>
                  <div className="lor-managers-stat-val lor-gold">
                    {championships || 0}
                    {championship_seasons?.length > 0 && (
                      <span className="lor-managers-stat-sub">
                        {' '}({championship_seasons.join(', ')})
                      </span>
                    )}
                  </div>
                </div>
                <div className="lor-managers-stat">
                  <div className="lor-managers-stat-label">Finals Appearances</div>
                  <div className="lor-managers-stat-val">{finals_appearances || 0}</div>
                </div>
                <div className="lor-managers-stat">
                  <div className="lor-managers-stat-label">Playoff Appearances</div>
                  <div className="lor-managers-stat-val">{playoff_appearances || 0}</div>
                </div>
                <div className="lor-managers-stat">
                  <div className="lor-managers-stat-label">Avg PPG</div>
                  <div className="lor-managers-stat-val lor-pf">{avg_ppg.toFixed(2)}</div>
                </div>
                {best_season && (
                  <div className="lor-managers-stat">
                    <div className="lor-managers-stat-label">Best Season</div>
                    <div className="lor-managers-stat-val lor-wins">
                      {best_season.season} ({best_season.wins}-{best_season.losses}, {(best_season.win_pct * 100).toFixed(0)}%)
                    </div>
                  </div>
                )}
                {worst_season && (
                  <div className="lor-managers-stat">
                    <div className="lor-managers-stat-label">Worst Season</div>
                    <div className="lor-managers-stat-val lor-losses">
                      {worst_season.season} ({worst_season.wins}-{worst_season.losses}, {(worst_season.win_pct * 100).toFixed(0)}%)
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function LORManagers({ api }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [sort, setSort]         = useState('championships');

  useEffect(() => {
    fetch(`${api}/api/lor/managers/records?sort=${sort}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(data => { setManagers(data || []); setLoading(false); })
      .catch(() => { setError('Failed to load manager records.'); setLoading(false); });
  }, [api, sort]);

  if (loading) return <div className="lor-loading">Loading manager records...</div>;
  if (error)   return <div className="lor-empty">{error}</div>;

  // Separate active and former (no is_former from this endpoint yet — show all)
  const active = managers.filter(m => !m.is_former);
  const former = managers.filter(m => m.is_former);
  const all    = [...active, ...former];

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="📊" size={18} /> All-Time Manager Records</h2>
        <div className="lor-page-meta">
          {managers.length} managers · all-time
        </div>
      </div>

      <div className="lor-managers-sort-bar">
        <span className="lor-managers-sort-label">Sort by:</span>
        {SORT_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`lor-managers-sort-btn${sort === opt.value ? ' active' : ''}`}
            onClick={() => setSort(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="lor-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="lor-standings-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>RK</th>
              <th>Manager</th>
              <th>W-L</th>
              <th>Win%</th>
              <th>PF</th>
              <th>PPG</th>
              <th>Streak</th>
              <th style={{ width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {all.map((m, i) => (
              <ManagerRow
                key={m.display_name}
                manager={m}
                rank={i + 1}
                isFormer={!!m.is_former}
              />
            ))}
          </tbody>
        </table>
      </div>

      {all.length === 0 && (
        <div className="lor-empty">No manager records available yet. History data is still loading.</div>
      )}

      <div className="lor-history-note">
        All-time records compiled from 2016–present. Former managers are included with a Former badge.
      </div>
    </div>
  );
}
